import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MessageCircle, Clock, Calendar } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { useLang } from "@/context/LanguageContext";
import { useData } from "@/context/DataContext";
import api from "@/lib/api";
import { openWa } from "@/lib/wa";

export default function ArticlePage() {
  const { slug } = useParams();
  const { t, pick } = useLang();
  const { categories } = useData();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api.get(`/articles/${slug}`)
      .then(({ data }) => {
        setPost(data);
        api.get("/articles", { params: { category: data.category, limit: 4 } }).then(({ data: rel }) => setRelated(rel.filter((r) => r.slug !== slug).slice(0, 3))).catch(() => {});
      })
      .catch(() => setNotFound(true));
    window.scrollTo(0, 0);
  }, [slug]);

  if (notFound) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-6 pt-[72px] text-center">
        <h1 className="text-2xl font-bold text-white">المقال غير موجود</h1>
        <Link to="/blog" className="btn-gold">المدونة</Link>
      </main>
    );
  }

  if (!post) return <div className="flex min-h-screen items-center justify-center pt-[72px]"><div className="h-10 w-10 animate-spin rounded-full border-2 border-gold border-t-transparent" /></div>;

  const catName = pick((categories.find((c) => c.id === post.category) || {}).name_ar, (categories.find((c) => c.id === post.category) || {}).name_en) || post.category;
  const date = new Date(post.created_at).toLocaleDateString(pick("ar-SA", "en-GB"), { year: "numeric", month: "long", day: "numeric" });

  return (
    <main>
      <SEO
        title={post.seo_title || `${post.title} | مدونة ميرو`}
        description={post.seo_desc || post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.image}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.seo_desc || post.excerpt,
          image: post.image,
          inLanguage: "ar",
          datePublished: post.created_at,
          author: { "@type": "Organization", name: "ميرو للإطارات وخدمات السيارات" },
          publisher: { "@type": "Organization", name: "ميرو للإطارات وخدمات السيارات" },
        }}
      />

      <article className="pt-32 sm:pt-40">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <Reveal>
            <div className="mb-5 flex flex-wrap items-center gap-3 text-xs">
              <span className="rounded-full bg-gold/10 px-3 py-1 font-bold text-gold">{catName}</span>
              <span className="flex items-center gap-1.5 text-dim"><Calendar size={12} /> {date}</span>
              <span className="flex items-center gap-1.5 text-dim"><Clock size={12} /> {post.read_time} {pick("دقائق", "min")}</span>
            </div>
            <h1 className="text-2xl font-extrabold leading-[1.6] text-white sm:text-3xl lg:text-4xl" data-testid="article-title">{post.title}</h1>
            <p className="mt-5 border-s-2 border-gold/50 ps-4 text-base leading-9 text-mist" data-testid="article-excerpt">{post.excerpt}</p>
          </Reveal>
        </div>

        <Reveal className="mx-auto mt-10 max-w-4xl px-5 lg:px-8">
          <img src={post.image} alt={post.title} className="w-full rounded-[2rem] border border-white/10 object-cover shadow-2xl shadow-black/50" data-testid="article-image" />
        </Reveal>

        <div className="mx-auto max-w-3xl px-5 py-12 lg:px-8">
          <div className="prose-mere" data-testid="article-content" dangerouslySetInnerHTML={{ __html: post.content }} />

          {post.tags?.length > 0 && (
            <div className="mt-10 flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <span key={tag} className="rounded-full border border-white/10 bg-coal/60 px-3.5 py-1.5 text-xs text-mist">{tag}</span>
              ))}
            </div>
          )}

          <Reveal className="mt-12">
            <div className="rounded-3xl border border-gold/25 bg-coal/70 p-8 text-center">
              <h2 className="text-lg font-bold text-white">{pick("هل تحتاج خدمة إطارات في الرياض؟", "Need a tire service in Riyadh?")}</h2>
              <p className="mx-auto mt-3 max-w-md text-sm leading-7 text-mist">{pick("راسلنا عبر واتساب واذكر لنا مشكلتك — سنؤكد التغطية والتكلفة قبل التنفيذ.", "Message us on WhatsApp with your issue — coverage and cost confirmed before we start.")}</p>
              <button
                onClick={() => openWa(`السلام عليكم، قرأت مقال "${post.title}" في مدونة ميرو، وأرغب في الاستفسار عن: ____.`)}
                className="btn-wa mt-6"
                data-testid="article-wa-cta"
              >
                <MessageCircle size={17} /> {t("blog.articleWa")}
              </button>
            </div>
          </Reveal>
        </div>

        {related.length > 0 && (
          <section className="border-t border-white/5 bg-coal/40 py-14">
            <div className="mx-auto max-w-7xl px-5 lg:px-8">
              <h2 className="mb-8 text-xl font-bold text-white" data-testid="related-articles-title">{t("blog.related")}</h2>
              <div className="grid gap-6 md:grid-cols-3">
                {related.map((r) => (
                  <Link key={r.slug} to={`/blog/${r.slug}`} className="group overflow-hidden rounded-3xl border border-white/10 bg-ink/70 transition-all hover:-translate-y-1 hover:border-gold/40" data-testid={`related-article-${r.slug}`}>
                    <div className="h-40 overflow-hidden">
                      <img src={r.image} alt={r.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <h3 className="p-5 text-sm font-bold leading-7 text-white group-hover:text-gold">{r.title}</h3>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
    </main>
  );
}
